import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useTeamStore } from "@/stores/team";
import { queryClient } from "@/lib/query";
import { Role, hasRole } from "@/features/team/lib/roles";
import { getTeamsServiceListTeamsQueryKey, teamsServiceListTeams } from "@/api/teams/teams";

declare module "vue-router" {
  interface RouteMeta {
    /** The caller must hold at least one of these roles in the current team. */
    requiredRoles?: Role[];
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "home",
      redirect: { name: "planning" },
    },
    {
      path: "/login",
      name: "login",
      meta: { isPublic: true, hideSidebar: true },
      component: () => import("@/features/identity/views/LoginView.vue"),
    },
    {
      path: "/register",
      name: "register",
      // Self-registration is closed until a payment gate exists (backend
      // ADR-0015). Point this back at RegisterView.vue when it reopens.
      redirect: { name: "login" },
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      meta: { isPublic: true, hideSidebar: true },
      component: () => import("@/features/identity/views/ForgotPasswordView.vue"),
    },
    {
      path: "/reset-password",
      name: "reset-password",
      meta: { isPublic: true, hideSidebar: true },
      component: () => import("@/features/identity/views/SetPasswordView.vue"),
    },
    {
      path: "/admin",
      name: "admin",
      // Platform-admin only, and reachable with no current team (the admin
      // likely belongs to no team) — requiresAuth only, not requiresTeam.
      meta: {
        requiresAuth: true,
        requiresAdmin: true,
        title: "routeTitles.admin",
      },
      component: () => import("@/features/admin/views/AdminView.vue"),
    },
    {
      path: "/account",
      name: "account",
      // requiresAuth only — reachable without a current team (minimal mode).
      meta: { requiresAuth: true, title: "routeTitles.account" },
      component: () => import("@/features/identity/views/AccountView.vue"),
    },
    {
      path: "/select-team",
      name: "select-team",
      meta: { requiresAuth: true, hideSidebar: true },
      component: () => import("@/features/team/views/SelectTeamView.vue"),
    },
    {
      path: "/no-team",
      name: "no-team",
      meta: { requiresAuth: true, hideSidebar: true },
      component: () => import("@/features/team/views/NoTeamView.vue"),
    },
    {
      path: "/team",
      name: "team",
      meta: {
        requiresAuth: true,
        requiresTeam: true,
        title: "routeTitles.team",
      },
      component: () => import("@/features/team/views/TeamView.vue"),
    },
    // Planning calendar — week/month schedule surface.
    {
      path: "/planning",
      name: "planning",
      meta: {
        requiresAuth: true,
        requiresTeam: true,
        requiredRoles: [Role.Coach, Role.Athlete],
        title: "routeTitles.planning",
      },
      component: () => import("@/features/planning/index.vue"),
    },
    // Workout editor — full-page modeless sheet (a primary feature, not a drawer).
    // No read/edit modes: every field edits in place, gated by canWrite.
    //   /planning/new[?date=YYYY-MM-DD][&group=<id>][&duplicate=<id>]  → new (blank or duplicate)
    //   /planning/workout/:id                                          → existing workout
    {
      path: "/planning/new",
      name: "workout-new",
      meta: {
        requiresAuth: true,
        requiresTeam: true,
        // Coach-only: athletes read the schedule, never author.
        requiredRoles: [Role.Coach],
        title: "routeTitles.workoutNew",
        hideHeader: true,
      },
      component: () => import("@/features/planning/views/WorkoutEditorView.vue"),
    },
    {
      path: "/planning/workout/:id",
      name: "workout-detail",
      meta: {
        requiresAuth: true,
        requiresTeam: true,
        requiredRoles: [Role.Coach, Role.Athlete],
        title: "routeTitles.workoutDetail",
        hideHeader: true,
      },
      component: () => import("@/features/planning/views/WorkoutEditorView.vue"),
    },
  ],
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // Hydrate (exchange refresh cookie → access token) at most once per session.
  if (!auth.hydrated) {
    await auth.hydrate();
  }

  if (to.meta.isPublic) {
    // If already authenticated, bounce away from login.
    if (auth.isLoggedIn && to.name === "login") {
      return { name: "home" };
    }
    return;
  }

  // Protected route — must be authenticated.
  if (!auth.isLoggedIn) {
    return { name: "login", query: { redirect: to.fullPath } };
  }

  // Admin-only route — non-admins get bounced home.
  if (to.meta.requiresAdmin && !auth.isPlatformAdmin) {
    return { name: "home" };
  }

  // Team-scoped route — a current team must be selected. Resolve the team count
  // here, before any view mounts, so a single-team user is never shown the
  // selector: 0 → no-team/admin, 1 → auto-select and continue, 2+ → pick.
  if (to.meta.requiresTeam) {
    const team = useTeamStore();
    if (!team.currentTeamId) {
      const res = await queryClient.fetchQuery({
        queryKey: getTeamsServiceListTeamsQueryKey(),
        queryFn: ({ signal }) => teamsServiceListTeams(signal),
        staleTime: 30_000,
      });
      const teams = res.teams ?? [];
      if (teams.length === 0) {
        return { name: auth.isPlatformAdmin ? "admin" : "no-team" };
      }
      if (teams.length === 1 && teams[0].id) {
        team.setCurrent(teams[0].id);
      } else {
        return { name: "select-team", query: { redirect: to.fullPath } };
      }
    }
  }

  // Role-gated route — the caller must hold one of the roles in the current
  // team (same ListTeams cache the sidebar reads). Managers have no planning
  // access, so they land on /team; anyone else without a fitting role on /account.
  if (to.meta.requiredRoles) {
    const res = await queryClient.fetchQuery({
      queryKey: getTeamsServiceListTeamsQueryKey(),
      queryFn: ({ signal }) => teamsServiceListTeams(signal),
      staleTime: 30_000,
    });
    const currentTeamId = useTeamStore().currentTeamId;
    const roles = res.teams
      ?.find((t) => t.id === currentTeamId)
      ?.members?.find((m) => m.id === auth.user?.id)?.roles;
    if (!to.meta.requiredRoles.some((r) => hasRole(roles, r))) {
      if (hasRole(roles, Role.Athlete)) return { name: "planning" };
      return { name: hasRole(roles, Role.Manager) ? "team" : "account" };
    }
  }
});

export default router;
